import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { signUp } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const ALLOWED_EMAIL_DOMAINS = ['slalom.com', 'gtue.de'];

const SignUp = ({ onSignUpSuccess, onBackToLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isEmailDomainAllowed = (emailAddress) => {
    const domain = emailAddress.toLowerCase().split('@')[1];
    if (!domain) return false;
    // Allow exact domain match or subdomain match (e.g., ext.gtue.de matches gtue.de)
    return ALLOWED_EMAIL_DOMAINS.some(
      allowed => domain === allowed || domain.endsWith(`.${allowed}`)
    );
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    // Validate email domain
    if (!isEmailDomainAllowed(email)) {
      setError(t('auth.error_email_not_allowed'));
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('auth.error_passwords_mismatch'));
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError(t('auth.error_password_too_short'));
      return;
    }

    setLoading(true);

    try {
      const result = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        onSignUpSuccess(email);
      }
    } catch (err) {
      console.error('SignUp error:', err);
      if (err.name === 'UsernameExistsException') {
        setError(t('auth.error_user_exists'));
      } else if (err.name === 'InvalidPasswordException') {
        setError(t('auth.error_invalid_password'));
      } else if (err.name === 'InvalidParameterException') {
        setError(t('auth.error_invalid_email'));
      } else {
        setError(t('auth.error_signup_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="card-elevated p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <Logo size={64} className="text-primary" animated />
            </div>
            <h1 className="text-2xl font-bold text-primary mt-4">
              {t('auth.create_account')}
            </h1>
            <p className="text-sm text-text-muted mt-1">
              {t('auth.register_for_holocron')}
            </p>
          </div>

          {/* SignUp Form */}
          <form onSubmit={handleSignUp} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                disabled={loading}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={loading}
                className="input"
              />
              <p className="text-xs text-text-light mt-1">
                {t('auth.min_password_length')}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.confirm_password')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={loading}
                className="input"
              />
            </div>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  {t('auth.signing_up')}
                </>
              ) : (
                t('auth.sign_up')
              )}
            </button>
          </form>

          {/* Back to Login Link */}
          <div className="text-center mt-6">
            <span className="text-sm text-text-muted">{t('auth.already_have_account')} </span>
            <button
              type="button"
              onClick={onBackToLogin}
              disabled={loading}
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              {t('auth.login')}
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-text-light opacity-70">
              © 2025 Holocron
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
